import { format, isBefore } from 'date-fns';
import appConstants from '../../../../appConstants';
import {
  getDeliveryStatusFromShipmentDelCode,
  getPendingStopStatus,
  getStopDeliveryStatusFromShipmentsDelCode,
  getShipmentStatusCodes
} from '../../Dispatch/utils/utils';
import { sortBy, pick } from 'lodash';
import { NO_FREIGHT, STOP_TYPE, DISPATCH_STATUS } from './constants';
import {
  NextStopComplianceStopMarker,
  NextStopCompliancePickupMarker,
  NextStopComplianceStopAPTMarker,
  NextStopComplianceStopLGMarker,
  NextStopCompliancePriorityMarker,
  CustomerLocationMarker,
  MarkerWithBadge
} from '../../../mapComponents/Markers';
import StyledToolTip from '../../../common/StyledToolTip';
import { getTimeDifferenceInMinutes } from '../NextStopCompliance/NextStopComplianceUtills';

const getTotalValue = (arr, key) => {
  return (
    arr.reduce((accumulator, object) => {
      return accumulator + object[key];
    }, 0) || 0
  );
};

const getDiffInMin = (startTime, endTime) => {
  if (startTime && endTime) {
    return getTimeDifferenceInMinutes(startTime.split(' ')[1], endTime.split(' ')[1]);
  }
  return null;
};

const getStopShipmentData = (stopData, key) => {
  return stopData.shipments?.length
    ? [...new Set(stopData.shipments.map((shipment) => shipment[key]))]?.toString()
    : '';
};
const pickupStopStatusCode = (stopData) => {
  return stopData.isNoFreight ? NO_FREIGHT : null;
};
const deliveryShipmentStatus = (shipment) => {
  const deliveryStatus = getDeliveryStatusFromShipmentDelCode(shipment.deliveryCode);
  switch (deliveryStatus) {
    case appConstants.DELIVERY_STATUS.RETURN:
      return shipment.returnCode;
    case appConstants.DELIVERY_STATUS.DELIVERY_EXCEPTION:
      return shipment.exceptionCode?.join(', ');
    default:
      return shipment.deliveryCode;
  }
};

//Returns the display label for a shipment's return/exception reason using client status lists.

const getStatusLabelForShipment = (
  shipment,
  staticClientStatusLists,
  enableReturnExceptionReasonLabels = false
) => {
  if (!enableReturnExceptionReasonLabels || !staticClientStatusLists) {
    return deliveryShipmentStatus(shipment);
  }
  const deliveryStatus = getDeliveryStatusFromShipmentDelCode(shipment.deliveryCode);
  const statusCodes = getShipmentStatusCodes(shipment);
  if (statusCodes.length === 0) {
    return [appConstants.DELIVERY_STATUS.RETURN, appConstants.DELIVERY_STATUS.DELIVERY_EXCEPTION].includes(deliveryStatus)
      ? null
      : shipment.deliveryCode;
  }
  const labels = statusCodes.map((code) =>
    getStatusTextForCode(code, deliveryStatus, staticClientStatusLists)
  );
  return labels.join(', ');
};

const pickupStopStatus = (stop) => {
  const { PICKUP_STATUS } = appConstants;
  return {
    status: stop.isNoFreight ? PICKUP_STATUS.NO_FREIGHT : PICKUP_STATUS.COMPLETED,
    hasReturnOrException: stop.isNoFreight
  };
};

const deliveryStopStatus = (stop) => {
  return {
    status: getStopDeliveryStatusFromShipmentsDelCode(stop.shipments),
    hasReturnOrException: hasReturnOrException(stop.shipments)
  };
};

const dropStopStatus = (stop) => {
  const { DELIVERY_STATUS } = appConstants;
  return {
    status: stop.isStopCompleted ? DELIVERY_STATUS.COMPLETED : 'TRAILER DROPPED',
    hasReturnOrException: false
  };
};

const stopStatus = (stopData) => {
  if (stopData.isDropStop) {
    return dropStopStatus(stopData);
  }
  if (stopData.isStopCompleted) {
    return stopData.stopType === STOP_TYPE.Pickup
      ? pickupStopStatus(stopData)
      : deliveryStopStatus(stopData);
  }
  return {
    status: appConstants.DELIVERY_STATUS.PENDING,
    hasReturnOrException: false
  };
};

const getUpdatedStopList = (stopList) => {
  let idCounter = 1;
  return stopList?.map((stop) => {
    const { consigneeDetails, serviceWindow, departureTime, arrivalTime, stopType, stopSequence } =
      stop;
    const isTrailerDrop = stopType === STOP_TYPE.TrailerDrop;
    const routeMaxUIStopIndex =
      isDeliveryOrPickup(stopType) || isTrailerDrop ? idCounter++ : stopSequence;
    const routeMaxUIStopSequence = isTrailerDrop ? '' : stopSequence;
    return {
      ...stop,
      name: consigneeDetails?.displayCustomerName,
      deliveryWindow: serviceWindow, //no need to format delivery window, as this is being used to calculate the pending stop status and there we are formatting it
      actualDepartureTime: departureTime,
      actualArrivalTime: arrivalTime,
      routeMaxUIStopIndex, // for stepper next amd prev button click
      routeMaxUIStopSequence // to show stop sequence in ui
    };
  });
};

const getStopListByRouteId = (driverData, selectedRouteId) => {
  if (driverData?.routes.length && selectedRouteId) {
    const stopList =
      driverData.routes.find((route) => route.routeId === selectedRouteId)?.stops || [];
    const updatedStops = getUpdatedStopList(stopList);
    return sortBy(updatedStops, 'stopSequence');
  }
  return [];
};

const getDriverData = (driverData) => {
  return pick(driverData, [
    'driverId',
    'driverName',
    'driverNumber',
    'lastKnownLatitude',
    'lastKnownLongitude',
    'isLiveDispatch',
    'loggedInStatus',
    'driverStatus',
    'terminal'
  ]);
};

const getRoute = (driverData, selectedRouteId) => {
  if (driverData?.routes?.length && selectedRouteId) {
    const routeData = driverData.routes.find((route) => route.routeId === selectedRouteId);
    if (routeData) {
      const { stops } = routeData;
      const updatedStops = getUpdatedStopList(stops)?.filter((stop) =>
        isDeliveryOrPickup(stop.stopType)
      );
      return {
        ...getDriverData(driverData),
        ...routeData,
        stops: updatedStops,        
        isLiveDispatch: driverData.isLiveDispatch && [DISPATCH_STATUS.Created,
          DISPATCH_STATUS.Started].includes(routeData.dispatchStatus)
      };
    }
    return null;
  }
};

const isDeliveryOrPickup = (stopType) =>
  [STOP_TYPE.Delivery, STOP_TYPE.Pickup].includes(stopType);

const isNotDelayStop = (stopType) =>
  [
    STOP_TYPE.Delivery,
    STOP_TYPE.Pickup,
    STOP_TYPE.TrailerDrop
  ].includes(stopType);

const getTimeString = (dateTime) => {
  return dateTime ? format(new Date(dateTime), appConstants.DATETYPES.TIME.format) : '-';
};

const getRouteStopList = (route) => {
  return (
    route.stops?.map((stop) => {
      return { ...stop, ...stop.consigneeDetails };
    }) || []
  );
};

const hasReturnOrException = (shipments) => {
  return (
    [
      ...new Set(shipments.map((shipment) => shipment.returnCode)),
      ...new Set(shipments.map((shipment) => shipment.exceptionCode))
    ].filter((data) => data).length > 0
  );
};

const getStopMarker = (stop) => {
  if (stop.stopType === STOP_TYPE.Pickup) {
    return NextStopCompliancePickupMarker;
  } else if (stop.priority) {
    return NextStopCompliancePriorityMarker;
  } else if (stop.isLiftGate) {
    return NextStopComplianceStopLGMarker;
  } else if (stop.isApt) {
    return NextStopComplianceStopAPTMarker;
  } else {
    return NextStopComplianceStopMarker;
  }
};

const renderLocationMarker = (stop, colors) => {
  return (
    <StyledToolTip title={stop.consigneeDetails.displayCustomerName}>
      <div>
        <CustomerLocationMarker
          backgroundColor={colors.color}
          borderColor={colors.textColor}
          strokeWidth="1"
        />
      </div>
    </StyledToolTip>
  );
};

const renderStopMarker = (stop, colors) => {
  const StopMarker = getStopMarker(stop);
  const badgeContent = getPickupStopBadgeContent(stop);
  return (
    <StyledToolTip title={stop.consigneeDetails.displayCustomerName}>
      <div>
        <MarkerWithBadge
          MarkerComponent={StopMarker}
          stopSequence={stop.routeMaxUIStopSequence}
          textColor={colors.textColor}
          color={colors.color}
          badgeContent={badgeContent}
        />
      </div>
    </StyledToolTip>
  );
};

const getPickupStopBadgeContent = (stop) => {
  if (!stop?.stopType || stop.stopType !== STOP_TYPE.Pickup) {
    return null;
  }
  const shipments = stop.shipments || [];
  const uniquePickupNumbers = Array.from(
    new Set(shipments.map((shipment) => shipment.pickupNumber).filter(Boolean))
  );
  return uniquePickupNumbers.length > 1 ? uniquePickupNumbers.length : null;
};

const checkIsBefore = (startTime, endTime) => {
  if (startTime && endTime) {
    return isBefore(new Date(startTime), new Date(endTime));
  }
  return true;
};

const getStatusTextForCode = (code, deliveryStatus, staticClientStatusLists) => {
  const findInList = (list, returnKey) => {
    return list.find((item) => item.value === code)?.[returnKey] || deliveryStatus;
  };

  switch (deliveryStatus) {
    case appConstants.DELIVERY_STATUS.COMPLETED:
      return findInList(staticClientStatusLists.deliveryStatusList, 'labelText');
    case appConstants.DELIVERY_STATUS.DELIVERY_EXCEPTION:
      return findInList(staticClientStatusLists.exceptionList, 'labelText');
    case appConstants.DELIVERY_STATUS.RETURN:
      return findInList(staticClientStatusLists.returnCodeList, 'label');
    default:
      return deliveryStatus;
  }
};

const getStopStatusList = (stopData, staticClientStatusLists) => {
  if (stopData.stopType === STOP_TYPE.Pickup) {
    return [`${stopData.bills || 0} Bills`];
  }

  const statusCounts = stopData.shipments?.reduce((acc, shipment) => {
    const deliveryStatus = getDeliveryStatusFromShipmentDelCode(shipment.deliveryCode);
    const statusCodes = getShipmentStatusCodes(shipment);

    if (statusCodes.length === 0) {
      const status = deliveryStatus;
      acc[status] = (acc[status] || 0) + 1;
    } else {
      statusCodes.forEach((code) => {
        const statusText = getStatusTextForCode(code, deliveryStatus, staticClientStatusLists);
        acc[statusText] = (acc[statusText] || 0) + 1;
      });
    }
    return acc;
  }, {});

  return Object.entries(statusCounts).map(([status, count]) => `${status} (${count})`);
};

const isDriverDelayed = (route) => {
  return route.stops?.findIndex((stop) => isDelayStop(stop)) > -1;
};

const isDelayStop = (stop) => {
  return (
    !stop?.isStopCompleted &&
    getPendingStopStatus(stop, stop.plannedArrivalTime?.split(' ')[1]) ===
      appConstants.STOP_STATUS.DELAYED
  );
};

const getStopDataByRouteMaxUIStopIndex = (stops, routeMaxUIStopIndex) => {
  return stops.find((stop) => stop.routeMaxUIStopIndex === routeMaxUIStopIndex);
};

const isActualLocationAvailable = (stopData) => stopData && stopData.latitude && stopData.longitude;

const getExistingFeaturesGeoJSON = ({ id, geofence }) => {
  return geofence
    ? [
        {
          id: id,
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [JSON.parse(geofence)]
          }
        }
      ]
    : [];
};

export {
  getStopListByRouteId,
  getRoute,
  getRouteStopList,
  getTimeString,
  getTotalValue,
  getDiffInMin,
  getStopShipmentData,
  stopStatus,
  pickupStopStatusCode,
  deliveryShipmentStatus,
  getStatusLabelForShipment,
  renderStopMarker,
  checkIsBefore,
  getStopStatusList,
  isDriverDelayed,
  isDelayStop,
  getStopDataByRouteMaxUIStopIndex,
  isDeliveryOrPickup,
  isNotDelayStop,
  isActualLocationAvailable,
  renderLocationMarker,
  getExistingFeaturesGeoJSON,
  getPickupStopBadgeContent
};
